import type { Faq, FaqCategory, Localized } from '../types/content'

export const faqCategories: { id: FaqCategory | 'all'; label: Localized }[] = [
  { id: 'all', label: { en: 'All questions', es: 'Todas' } },
  { id: 'getting-started', label: { en: 'Getting started', es: 'Primeros pasos' } },
  { id: 'services', label: { en: 'Services', es: 'Servicios' } },
  { id: 'pricing', label: { en: 'Pricing', es: 'Precios' } },
  { id: 'process', label: { en: 'Care process', es: 'Proceso de cuidado' } },
  { id: 'scheduling', label: { en: 'Scheduling', es: 'Horarios' } },
]

export const faqs: Faq[] = [
  {
    id: 'get-started',
    category: 'getting-started',
    preview: true,
    question: { en: 'How do I get started with A.M. Home Care?', es: '¿Cómo empiezo con A.M. Home Care?' },
    answer: {
      en: "Call us at (703) 231-7468 or email a.m.homecare.virginia@gmail.com. We'll schedule a consultation to discuss your loved one's needs. WhatsApp is also available for messages.",
      es: 'Llámanos al (703) 231-7468 o envía un correo a a.m.homecare.virginia@gmail.com. Programaremos una consulta para conversar sobre las necesidades de tu ser querido. También puedes escribirnos por WhatsApp.',
    },
  },
  {
    id: 'services-offered',
    category: 'services',
    preview: false,
    question: { en: 'What services do you offer?', es: '¿Qué servicios ofrecen?' },
    answer: {
      en: "We offer personal care assistance, companionship and homemaking, medication reminders, transportation and errands, specialized care for Alzheimer's, dementia, and Parkinson's, and respite care for family caregivers. Call to discuss specific needs.",
      es: 'Ofrecemos asistencia de cuidado personal, compañerismo y tareas domésticas, recordatorios de medicamentos, transporte y encargos, cuidado especializado para Alzheimer, demencia y Parkinson, y cuidado de relevo para cuidadores familiares. Llámanos para conversar sobre necesidades específicas.',
    },
  },
  {
    id: 'available-247',
    category: 'scheduling',
    preview: true,
    question: { en: 'Are you available 24/7?', es: '¿Están disponibles 24/7?' },
    answer: {
      en: "Yes, we're available 24 hours a day, 7 days a week. We can accommodate your schedule whether you need daytime, nighttime, or round-the-clock care.",
      es: 'Sí, estamos disponibles 24 horas al día, 7 días a la semana. Nos adaptamos a tu horario, ya sea que necesites cuidado de día, de noche o las 24 horas.',
    },
  },
  {
    id: 'bilingual',
    category: 'services',
    preview: false,
    question: { en: 'Do you have bilingual caregivers?', es: '¿Tienen cuidadores bilingües?' },
    answer: {
      en: 'Yes. We have English and Spanish speaking caregivers available. Se habla español. Communication in your preferred language is important to us.',
      es: 'Sí. Tenemos cuidadores que hablan inglés y español. La comunicación en tu idioma preferido es muy importante para nosotros.',
    },
  },
  {
    id: 'dementia',
    category: 'services',
    preview: true,
    question: {
      en: "Do you specialize in Alzheimer's and dementia care?",
      es: '¿Se especializan en cuidado de Alzheimer y demencia?',
    },
    answer: {
      en: "Yes. Our caregivers receive specialized training in dementia care. We understand the unique needs and challenges of caring for someone with Alzheimer's or dementia.",
      es: 'Sí. Nuestros cuidadores reciben capacitación especializada en cuidado de demencia. Entendemos las necesidades y los desafíos únicos de cuidar a alguien con Alzheimer o demencia.',
    },
  },
  {
    id: 'cost',
    category: 'pricing',
    preview: true,
    question: { en: 'How much does care cost?', es: '¿Cuánto cuesta el cuidado?' },
    answer: {
      en: 'Pricing depends on the type of care, hours needed, and specific services. Most hourly care falls between $30 and $38 per hour. We offer flexible options. Call for a free consultation and pricing estimate.',
      es: 'Los precios dependen del tipo de cuidado, las horas necesarias y los servicios específicos. La mayoría del cuidado por hora está entre $30 y $38. Ofrecemos opciones flexibles. Llama para una consulta gratuita y un estimado.',
    },
  },
  {
    id: 'insurance',
    category: 'pricing',
    preview: false,
    question: {
      en: 'Do you work with long-term care insurance?',
      es: '¿Trabajan con seguros de cuidado a largo plazo?',
    },
    answer: {
      en: 'Many families use long-term care insurance to help pay for in-home care. We can provide care documentation and invoices to support your claims. Call us to talk through your policy.',
      es: 'Muchas familias usan su seguro de cuidado a largo plazo para ayudar a pagar el cuidado en casa. Podemos proporcionar documentación y facturas para apoyar tus reclamos. Llámanos para revisar tu póliza.',
    },
  },
  {
    id: 'licensed',
    category: 'getting-started',
    preview: false,
    question: { en: 'Are you licensed and insured?', es: '¿Están licenciados y asegurados?' },
    answer: {
      en: 'Yes. We are fully licensed, bonded, and insured. Your peace of mind and security are our priority.',
      es: 'Sí. Estamos completamente licenciados, afianzados y asegurados. Tu tranquilidad y seguridad son nuestra prioridad.',
    },
  },
  {
    id: 'matching',
    category: 'process',
    preview: false,
    question: { en: 'How do you choose my loved one’s caregiver?', es: '¿Cómo eligen al cuidador de mi ser querido?' },
    answer: {
      en: 'After the consultation we match your loved one with a caregiver whose skills, language, and personality fit their needs. If the match ever doesn’t feel right, tell us and we’ll make a change.',
      es: 'Después de la consulta asignamos un cuidador cuyas habilidades, idioma y personalidad se ajusten a las necesidades de tu ser querido. Si en algún momento no te sientes cómodo, avísanos y haremos un cambio.',
    },
  },
  {
    id: 'backup',
    category: 'process',
    preview: false,
    question: { en: 'What happens if my caregiver is sick?', es: '¿Qué pasa si el cuidador se enferma?' },
    answer: {
      en: 'We arrange a qualified backup caregiver who has been briefed on your loved one’s care plan, so care continues without interruption.',
      es: 'Asignamos un cuidador de respaldo calificado que conoce el plan de cuidado de tu ser querido, para que el cuidado continúe sin interrupción.',
    },
  },
  {
    id: 'schedule-consult',
    category: 'scheduling',
    preview: false,
    question: { en: 'Can I schedule a consultation?', es: '¿Puedo agendar una consulta?' },
    answer: {
      en: "Absolutely. Call (703) 231-7468, text/WhatsApp, or email to schedule. You can also use the form on our Contact page. We're happy to discuss your loved one's care needs.",
      es: 'Por supuesto. Llama al (703) 231-7468, envía un mensaje de texto o WhatsApp, o escribe un correo para agendar. También puedes usar el formulario de nuestra página de Contacto.',
    },
  },
  {
    id: 'start-time',
    category: 'scheduling',
    preview: false,
    question: { en: 'How quickly can care begin?', es: '¿Qué tan pronto puede comenzar el cuidado?' },
    answer: {
      en: 'In many cases care can begin within 24–48 hours of your consultation, and sooner for urgent situations such as a hospital discharge.',
      es: 'En muchos casos el cuidado puede comenzar entre 24 y 48 horas después de la consulta, y antes en situaciones urgentes como un alta hospitalaria.',
    },
  },
]
